<template>
	<div class="last-step">
		<template v-if="error">
			<h3>Fehler bei der Anlage</h3>
			<div>
				Die Anlage deines Anonymprofils hat leider nicht funktioniert. Bitte gehe einen Schritt zurück und prüfe
				deine Daten (vor allem die E-Mail-Adresse).
			</div>
			<div class="mt-3">
				<Button text="Zurück" icon="pencil" @click="$emit('goBack')"/>
			</div>
		</template>
		<template v-else>
			<template v-if="loading">
				<Loading :fun-fact="false"/>
				<div class="mt-4 text-center">
					<h3>Dein Anonymprofil wird angelegt, das dauert ein paar Augenblicke.<br/>Bitte schließe diese Seite
						nicht.</h3>
				</div>
			</template>
			<template v-else>
				<h1>{{ title }}</h1>
				<div class="intro">
					<p>
						Wir haben dir ein E-Mail gesendet an<br/>
						<span class="pl-4 color-primary">{{ emailToShow }}</span><br/>
					</p>
					<p>Bitte klicke im E-Mail den Link, um dein Profil zu aktivieren und passende Jobs zu finden.</p>
				</div>
				<template v-if="application">
					<div class="action-container">
						<div>Kein E-Mail erhalten und im Spamordner/Junkordner ist kein E-Mail gekommen?</div>
						<div class="pl-4">
							<Loading v-if="resending" small inline :block-element="false"/>
							<a v-else class="icon-link" href="#" @click.prevent="resend">
								<Icon icon="mail"/>
								Neu senden
							</a>
						</div>
					</div>
					<div class="action-container">
						<div>E-Mail Adresse falsch?</div>
						<div class="pl-4">
							<a class="icon-link" href="#" @click.prevent="correctEmail= true">
								<Icon icon="settings"/>
								E-Mail Adresse bearbeiten
							</a>
							<FormGroup
								v-if="correctEmail"
								:state="emailValid"
								:invalid-feedback="emailError"
								feedback-beneath
							>
								<div class="correct-email">
									<div>
										<b-input v-model="email" type="email"/>
									</div>
									<div>
										<Button
											text="E-Mail bestätigen"
											icon="checkmark"
											:disabled="!emailValid || resending"
											@click="resend"
										/>
									</div>
								</div>
							</FormGroup>
						</div>
					</div>
				</template>
			</template>
		</template>
	</div>
</template>

<script>
import FormGroup from "@/vue/common/form/Group";
import Icon from "@/vue/icon/Icon";
import Init from "@/api/application/Init";
import * as Preparation from "@/helper/application/Preparation";
import Loading from "@/vue/common/Loading";
import {isDefined} from "@/helper/Value";
import {isEmptyOrNull} from "@/helper/String";
import * as EmailValidator from 'email-validator';
import Button from "@/vue/common/Button";
import * as Toast from "@/helper/layout/Toast";
import flatten from "@/helper/error/Errors";
import Resend from "@/api/application/email/Resend";
import {isEligible} from "@/helper/characteristic/CareerLevel";
import _ from "lodash";
import CheckEmailConfirmation from "@/api/application/security/CheckEmailConfirmation";

const CONFIRMATION_INTERVAL = 5;
const MAX_CONFIRMATION_CHECKS = (60 / CONFIRMATION_INTERVAL) * 15; // every 5 seconds for 15 minutes

export default {

	components: {
		Button,
		Loading,
		FormGroup,
		Icon,
	},

	mounted ()
	{
		if (typeof window.fbq !== 'undefined')
		{
			window.fbq('track', 'CompleteRegistration');
		}

		if (typeof gtag !== 'undefined')
		{
			gtag('event', 'conversion', {'send_to': 'AW-16707736154/4wK8CP-dutYZENqk754-'});
		}

		this.initApplication();
	},

	props: {
		title: {
			type: String,
			required: true,
		},
		formData: {
			type: Object,
			required: true,
		},
	},

	data ()
	{
		const email = this.formData.step6.personalData.email;

		return {
			loading: true,
			application: undefined,
			correctEmail: false,
			email,
			emailToShow: email,
			resending: false,
			confirmationChecks: 0,
			error: false,
		};
	},

	computed: {
		emailValid ()
		{
			return !isDefined(this.emailError);
		},
		emailError ()
		{
			if (isEmptyOrNull(this.email))
			{
				return 'Pflichtfeld';
			}

			if (!EmailValidator.validate(this.email))
			{
				return 'E-Mail Adresse unvollständig/ungültig';
			}

			return null;
		},
	},

	methods: {
		async resend ()
		{
			if (this.resending)
			{
				return;
			}

			this.resending = true;

			try
			{
				await new Resend()
					.setApplicationId(this.application.id)
					.setApplicationSecurityToken(this.application.securityToken)
					.setEmail(this.email)
					.call();

				this.emailToShow = this.email;
				this.correctEmail = false;
			}
			catch (e)
			{
				Toast.error(this, flatten(e.errors));
			}

			this.resending = false;
		},
		async initApplication ()
		{
			const careerLevelEligible = isEligible(
				_.map(this.formData.step1.employmentModes, 'item')
			);

			try
			{
				const response = await new Init()
					.setFunctions(
						Preparation.prepareCharacteristicWithExperience(this.formData.step4.functions)
					)
					.setCareerLevels(
						careerLevelEligible
							? Preparation.prepareCharacteristicWithExperience(this.formData.step3.careerLevels)
							: undefined
					)
					.setIndustrialSectors(
						this.formData.step5?.industrialSectors
							? Preparation.prepareCharacteristicWithExperience(this.formData.step5.industrialSectors)
							: []
					)
					.setOrganisationalArea(
						this.formData.step5?.organisationalArea
							? Preparation.prepareCharacteristicWithExperience(this.formData.step5.organisationalArea)
							: undefined
					)
					.setEmploymentModes(
						Preparation.prepareCharacteristic(this.formData.step1.employmentModes)
					)
					.setWorkingTime({
						workingTime: Preparation.prepareCharacteristic(this.formData.step1.workingTimes)
					})
					.setGeo(
						Preparation.prepareCharacteristic(this.formData.step2.geo)
					)
					.setHomeOffice(this.formData.step2.homeOffice)
					.setPersonalData(this.formData.step6.personalData)
					.setSecurity({
						password: this.formData.step6.password
					})
					.call();

				this.application = response.data;

				this.checkConfirmationInterval = setInterval(async () =>
				{
					try
					{
						const response = await new CheckEmailConfirmation()
							.setId(this.application.id)
							.setSecurityToken(this.application.securityToken)
							.call();

						if (response.data.confirmed)
						{
							location.href = '/auth/login?activated=1&email=' + this.emailToShow;
						}
					}
					catch (e)
					{
						// do nothing
					}

					this.confirmationChecks++;

					if (this.confirmationChecks === MAX_CONFIRMATION_CHECKS)
					{
						clearInterval(this.checkConfirmationInterval);
					}

				}, CONFIRMATION_INTERVAL * 1000);
			}
			catch (e)
			{
				this.error = true;
			}

			this.loading = false;
		},

	}
}
</script>
